import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { brandGroupIdAtom } from '~/atoms'
import {
  BrandSelector,
  type BrandSelectorProps,
} from '~/components/features/BrandSelector'
import {
  type Brand,
  useSurveyQueryAvailableBrandGroups,
} from '~/hooks/useSurveyQuery'
import { exists } from '~/utils'

import { brandIdsAtom, isPendingTabulationAtom } from '../atoms'

export const BrandSelectorContainer = () => {
  const { t } = useTranslation()
  const brandGroups = useSurveyQueryAvailableBrandGroups()

  const brandGroupId = useAtomValue(brandGroupIdAtom)

  const [brandIds, setBrandIds] = useAtom(brandIdsAtom)

  const setIsPendingTabulation = useSetAtom(isPendingTabulationAtom)

  const unselectedBrands = React.useMemo(
    () =>
      brandGroups
        .find(({ id }) => id === brandGroupId)
        ?.brands?.filter(
          (brand): brand is Brand =>
            exists(brand) && !brandIds.includes(brand.id),
        ) ?? [],
    [brandGroupId, brandGroups, brandIds],
  )

  const handleChange = React.useCallback<
    EH<BrandSelectorProps<Brand>, 'onChange'>
  >(
    (brandId) => {
      setBrandIds((brandIds) => [...brandIds, brandId])
      setIsPendingTabulation(false)
    },
    [setBrandIds, setIsPendingTabulation],
  )

  return (
    <BrandSelector
      brands={unselectedBrands}
      disabled={unselectedBrands.length === 0}
      isNullSelectable={true}
      nullText={t('add_brand')}
      value={null}
      width={360}
      onChange={handleChange}
    />
  )
}
